import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api'
import styles from './CharityLocationsMap.module.scss'

const DEFAULT_ZOOM = 11
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%', minHeight: 420 }

const LOCATION_TYPES = [
  { key: 'volunteer_location', type: 'volunteer' },
  { key: 'donation_location', type: 'donation' },
  { key: 'visitor_location', type: 'visitor' },
]

function pinsFromCharity(charity) {
  if (!charity) return []
  return LOCATION_TYPES.reduce((acc, { key, type }) => {
    const loc = charity[key]
    if (
      loc &&
      loc.latitude != null &&
      loc.longitude != null &&
      !Number.isNaN(Number(loc.latitude)) &&
      !Number.isNaN(Number(loc.longitude))
    ) {
      acc.push({
        id: loc.id ?? type,
        type,
        name: loc.name,
        formatted_address: loc.formatted_address,
        lat: Number(loc.latitude),
        lng: Number(loc.longitude),
      })
    }
    return acc
  }, [])
}

export default function CharityLocationsMap({ charity, className }) {
  const { t } = useTranslation()
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  const [selectedId, setSelectedId] = useState(null)

  const pins = useMemo(() => pinsFromCharity(charity), [charity])

  const mapCenter = useMemo(() => {
    if (
      charity?.latitude != null &&
      charity?.longitude != null &&
      !Number.isNaN(Number(charity.latitude)) &&
      !Number.isNaN(Number(charity.longitude))
    ) {
      return { lat: Number(charity.latitude), lng: Number(charity.longitude) }
    }
    if (pins.length > 0) {
      return { lat: pins[0].lat, lng: pins[0].lng }
    }
    return undefined
  }, [charity?.latitude, charity?.longitude, pins])

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-charity-profile',
    googleMapsApiKey: apiKey || '',
  })

  const selectedPin = useMemo(
    () => (selectedId != null ? pins.find(p => p.id === selectedId) : null),
    [pins, selectedId],
  )

  if (!pins.length) {
    return null
  }

  if (!apiKey) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapNotConfigured', 'Map is not configured.')}
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapLoadError', 'Map could not be loaded.')}
        </div>
      </div>
    )
  }

  if (!isLoaded || !mapCenter) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapLoading', 'Loading map…')}
        </div>
      </div>
    )
  }

  return (
    <div className={className || styles.mapContainer}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          fullscreenControl: true,
        }}
      >
        {pins.map(pin => (
          <Marker
            key={pin.id}
            position={{ lat: pin.lat, lng: pin.lng }}
            title={pin.name || pin.type}
            onClick={() => setSelectedId(pin.id)}
          />
        ))}
        {selectedPin ? (
          <InfoWindow
            position={{ lat: selectedPin.lat, lng: selectedPin.lng }}
            onCloseClick={() => setSelectedId(null)}
          >
            <div className={styles.infoWindow}>
              <div className={styles.infoWindowType}>
                {t(`charities.profile.location_${selectedPin.type}`, selectedPin.type)}
              </div>
              {selectedPin.name ? (
                <div className={styles.infoWindowTitle}>{selectedPin.name}</div>
              ) : null}
              {selectedPin.formatted_address ? (
                <div className={styles.infoWindowAddress}>
                  {selectedPin.formatted_address}
                </div>
              ) : null}
            </div>
          </InfoWindow>
        ) : null}
      </GoogleMap>
    </div>
  )
}

CharityLocationsMap.propTypes = {
  charity: PropTypes.object,
  className: PropTypes.string,
}
